import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Building2, CheckCircle2, Clock, TrendingUp, AlertTriangle, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { useSuperAdmin } from '../../context/SuperAdminContext';

const StatCard = ({ title, value, icon: Icon, color, delay = 0 }) => {
  const colorMap = {
    blue: 'from-blue-500 to-blue-600 shadow-blue-500/25',
    green: 'from-emerald-500 to-emerald-600 shadow-emerald-500/25',
    amber: 'from-amber-500 to-amber-600 shadow-amber-500/25',
    purple: 'from-purple-500 to-purple-600 shadow-purple-500/25',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-800">{value ?? '...'}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorMap[color]} shadow-lg flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );
};

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="space-y-3">
        <div className="h-4 w-24 bg-gray-200 rounded" />
        <div className="h-8 w-16 bg-gray-200 rounded" />
      </div>
      <div className="w-12 h-12 bg-gray-200 rounded-xl" />
    </div>
  </div>
);

const AdminDashboard = () => {
  const { adminUser } = useSuperAdmin();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await superAdminAPI.getDashboard();
      setData(response.data?.data || response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      active: { label: 'نشط', cls: 'bg-emerald-100 text-emerald-700' },
      trial: { label: 'تجريبي', cls: 'bg-amber-100 text-amber-700' },
      expired: { label: 'منتهي', cls: 'bg-red-100 text-red-700' },
      suspended: { label: 'موقوف', cls: 'bg-red-100 text-red-700' },
    };
    const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
  };

  return (
    <div>
      {/* Welcome */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <h1 className="text-2xl font-bold text-gray-800">
          مرحبا, {adminUser?.name || 'Admin'} 👋
        </h1>
        <p className="text-gray-500 text-sm mt-1">لوحة تحكم المشرف العام</p>
      </motion.div>

      {/* Stats */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="إجمالي المختبرات"
            value={data?.totalTenants ?? 0}
            icon={Building2}
            color="blue"
            delay={0}
          />
          <StatCard
            title="المختبرات النشطة"
            value={data?.activeTenants ?? 0}
            icon={CheckCircle2}
            color="green"
            delay={0.05}
          />
          <StatCard
            title="التجريبية"
            value={data?.trialTenants ?? 0}
            icon={Clock}
            color="amber"
            delay={0.1}
          />
          <StatCard
            title="MRR (الإيراد الشهري)"
            value={(data?.MRR || data?.mrr) != null ? `${(data.MRR || data.mrr).toLocaleString()} ر.س` : '0 ر.س'}
            icon={TrendingUp}
            color="purple"
            delay={0.15}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expiring Soon */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h2 className="font-semibold text-gray-800">اشتراكات تنتهي قريبا</h2>
          </div>
          <div className="p-4">
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse flex items-center justify-between p-3">
                    <div className="h-4 w-32 bg-gray-200 rounded" />
                    <div className="h-4 w-20 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            ) : data?.expiringSoon?.length > 0 ? (
              <div className="divide-y divide-gray-50">
                {data.expiringSoon.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/tenants/${item.tenantId?._id || item.tenantId || item._id}`)}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.tenantName || item.tenantId?.name || '-'}</p>
                      <p className="text-xs text-gray-400">{item.planName || item.planId?.name || '-'}</p>
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-red-500 font-medium">
                        {item.daysRemaining != null
                          ? `${item.daysRemaining} يوم متبقي`
                          : new Date(item.endDate).toLocaleDateString('ar-SA-u-ca-gregory')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400 text-sm">
                لا توجد اشتراكات تنتهي قريبا
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Tenants */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-500" />
              <h2 className="font-semibold text-gray-800">أحدث المختبرات</h2>
            </div>
            <button
              onClick={() => navigate('/admin/tenants')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              عرض الكل
            </button>
          </div>
          <div className="p-4">
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse flex items-center justify-between p-3">
                    <div className="h-4 w-32 bg-gray-200 rounded" />
                    <div className="h-4 w-20 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            ) : data?.recentTenants?.length > 0 ? (
              <div className="divide-y divide-gray-50">
                {data.recentTenants.slice(0, 5).map((tenant, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/tenants/${tenant._id}`)}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">{tenant.name}</p>
                      <p className="text-xs text-gray-400">{tenant.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {statusBadge(tenant.isActive === false ? 'suspended' : 'active')}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400 text-sm">
                لا توجد مختبرات
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
